// VOD categories (Twitch «главы» — game changes) for the heatmap zoom bar. Pure, no Angular.

export interface VodChapter {
  startSec: number;
  endSec: number;
  gameId: string;
  name: string;
}

/**
 * GQL `video { lengthSeconds game moments(momentRequestType: VIDEO_CHAPTER_MARKERS) }` →
 * chapters sorted by start. A VOD that stayed in one category has NO chapter markers —
 * then it's one chapter of `video.game` (note: on a multi-category VOD `video.game` is the
 * FIRST category, not the current one). Adjacent chapters of the same game are merged.
 */
export function chaptersFromGqlVideo(video: any): VodChapter[] {
  const length = Number(video?.lengthSeconds) || 0;
  const chapters: VodChapter[] = [];
  for (const edge of video?.moments?.edges ?? []) {
    const node = edge?.node;
    if (node?.type !== 'GAME_CHANGE') continue;
    const game = node.details?.game;
    const name = String(game?.displayName || game?.name || node.description || '').trim();
    const startSec = Math.max(0, (Number(node.positionMilliseconds) || 0) / 1000);
    const durSec = (Number(node.durationMilliseconds) || 0) / 1000;
    if (!name || !(durSec > 0)) continue;
    chapters.push({ startSec, endSec: startSec + durSec, gameId: String(game?.id || name), name });
  }
  if (chapters.length === 0) {
    const game = video?.game;
    const name = String(game?.displayName || game?.name || '').trim();
    if (name && length > 0) chapters.push({ startSec: 0, endSec: length, gameId: String(game?.id || name), name });
    return chapters;
  }
  chapters.sort((a, b) => a.startSec - b.startSec);
  const merged: VodChapter[] = [];
  for (const c of chapters) {
    const prev = merged[merged.length - 1];
    if (prev && prev.gameId === c.gameId && c.startSec <= prev.endSec + 1) {
      prev.endSec = Math.max(prev.endSec, c.endSec);
    } else {
      merged.push({ ...c });
    }
  }
  // A live VOD keeps growing past the last chapter's duration.
  const last = merged[merged.length - 1];
  if (length > last.endSec) last.endSec = length;
  return merged;
}

/** The chapter playing at `t` (null outside every chapter). */
export function chapterAt(chapters: VodChapter[], t: number): VodChapter | null {
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (chapters[i].startSec <= t) return t < chapters[i].endSec ? chapters[i] : null;
  }
  return null;
}
